<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import ScrollChapterUnlock from './ScrollChapterUnlock.vue'
import AdSenseInlineArticle from '@/components/AdSenseInlineArticle.vue'
import { CHAPTER_LOCK } from '@/constants'
import { brand } from '@/config/brands'

interface Props {
  chapter: ScrollChapter
  contentId: string
  themeFont: {
    chapterTitle: string
    chapterContent: string
    paragraphSpacing: string
  }
  memberCoin: number
  isLastChapter?: boolean
  bookName?: string
  serialStatus?: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'unlock', chapterId: string, autoLock: boolean): void
  (e: 'pay-success'): void
}>()

const unlockRef = ref<InstanceType<typeof ScrollChapterUnlock>>()
const isTalewaveDivider = computed(() => brand === 'talewave')
const talewaveBookDividerIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-book.svg)`)

const paragraphs = computed(() => {
  if (!props.chapter.content)
    return []
  return props.chapter.content.split(/[\n]/).filter(Boolean)
})

const contentItems = computed(() => {
  if (!paragraphs.value.length)
    return []
  const items: Array<{ type: 'paragraph'; text: string } | { type: 'ad' }> = []
  let acc = 0
  for (const text of paragraphs.value) {
    items.push({ type: 'paragraph', text })
    acc += text.length
    if (brand === 'rocnovel' && acc > 200) {
      items.push({ type: 'ad' })
      acc = 0
    }
  }
  // eslint-disable-next-line no-console
  console.log('[AdSense] scroll contentItems, paragraphs=', paragraphs.value.length, 'ads=', items.filter(i => i.type === 'ad').length)
  return items
})

const isLocked = computed(() => props.chapter.lockStatus === CHAPTER_LOCK.LOCK)
const dividerIcon = computed(() => `/images/${brand}/chapter/chapter-divider.png`)

function handleUnlock() {
  const autoLock = unlockRef.value?.autoLock ?? true
  emit('unlock', props.chapter.id, autoLock)
}
</script>

<template>
  <div class="chapter-item px-20px py-10">
    <!-- 章节标题 -->
    <h2 :style="themeFont.chapterTitle" class="chapter-title font-800">
      {{ chapter.name }}
    </h2>

    <!-- 章节内容 -->
    <div :style="themeFont.chapterContent" class="chapter-content">
      <div v-for="(item, idx) in contentItems" :key="idx">
        <p v-if="item.type === 'paragraph'" :style="themeFont.paragraphSpacing">
          {{ item.text }}
        </p>
        <AdSenseInlineArticle v-else />
      </div>
    </div>

    <!-- 付费解锁区域（仅锁定章节显示） -->
    <ScrollChapterUnlock
      v-if="isLocked"
      ref="unlockRef"
      :content-id="contentId"
      :chapter="chapter"
      :member-coin="memberCoin"
      :book-name="bookName"
      :serial-status="serialStatus"
      @unlock="handleUnlock"
      @pay-success="emit('pay-success')"
    />

    <!-- 章节结束分隔符（最后一章不显示） -->
    <div v-if="!isLastChapter" class="chapter-divider">
      <span class="divider-line divider-line-left" :class="{ 'divider-line-talewave divider-line-talewave-left': isTalewaveDivider }" />
      <span v-if="isTalewaveDivider" class="divider-icon divider-icon-talewave" aria-hidden="true" />
      <img v-else :src="dividerIcon" class="divider-icon" alt="">
      <span class="divider-line divider-line-right" :class="{ 'divider-line-talewave divider-line-talewave-right': isTalewaveDivider }" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chapter-item {
  min-height: 50vh;
}

.chapter-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  gap: 12px;
  position: relative;
  top: 36px;
}

.divider-icon {
  width: 18px;
  flex-shrink: 0;
}

.divider-icon-talewave {
  width: 20px;
  height: 20px;
  display: inline-block;
  background-color: var(--unlock-divider-color, #A8AFBB);
  -webkit-mask-image: v-bind(talewaveBookDividerIcon);
  mask-image: v-bind(talewaveBookDividerIcon);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.divider-line {
  flex: 1;
  max-width: 120px;
  height: 1px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.divider-line-left {
  &::before {
    background: linear-gradient(to right, #AAB1B8, #9296A0);
    clip-path: polygon(0 25%, 100% 0, 100% 100%, 0 75%);
  }
}

.divider-line-right {
  &::before {
    background: linear-gradient(to right, #9296A0, #AAB1B8);
    clip-path: polygon(0 0, 100% 25%, 100% 75%, 0 100%);
  }
}

.divider-line-talewave {
  height: 1px;

  &::before {
    background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
    clip-path: none;
  }
}

.divider-line-talewave-left {
  &::before {
    background: linear-gradient(to left, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
  }
}

.divider-line-talewave-right {
  &::before {
    background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
  }
}
</style>
